import React, { useState, useEffect } from 'react'
import { Eye, Instagram, Linkedin } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import MegaMenu from './MegaMenu'

const HERO_DETAILS_CHANGE_EVENT = 'debeach:hero-details-change'
const SHOW_HERO_DETAILS_EVENT = 'debeach:show-hero-details'

const NAV_ITEMS: { label: string; href: string }[] = [
  { label: 'Listings', href: '#listings' },
  { label: 'Home Tours', href: '#home-tours' },
  { label: 'Map', href: '/listings' },
  { label: 'Beach Towns', href: '/areas' },
  { label: 'Sellers', href: '/sell' },
  { label: 'Buyers', href: '/buy' },
  { label: 'Our Story', href: '/about' },
  { label: 'Contact', href: '#contact' },
]

const Header: React.FC = () => {
  const { pathname } = useLocation()
  // P3.7: hash anchors only exist on Home — off-home routes need `/#section`
  const resolve = (href: string): string =>
    href.startsWith('#') && pathname !== '/' ? `/${href}` : href
  const navItems = NAV_ITEMS.map((item) => ({ ...item, href: resolve(item.href) }))
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [heroDetailsVisible, setHeroDetailsVisible] = useState(true)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 80)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const handleHeroDetailsChange = (event: Event) => {
      setHeroDetailsVisible(Boolean((event as CustomEvent<{ visible?: boolean }>).detail?.visible))
    }
    window.addEventListener(HERO_DETAILS_CHANGE_EVENT, handleHeroDetailsChange)
    return () => window.removeEventListener(HERO_DETAILS_CHANGE_EVENT, handleHeroDetailsChange)
  }, [])

  const solid = pathname !== '/' || isScrolled || isMegaMenuOpen || isMobileMenuOpen

  return (
    <>
    <header
      className={`fixed top-0 left-0 right-0 z-50 text-white transition-all duration-300 ${
        solid ? 'bg-primary-ink shadow-lg' : 'bg-transparent'
      } ${isScrolled ? 'h-[52px]' : 'h-16'}`}
    >
      <div className="h-full max-w-7xl mx-auto px-4 sm:px-6">
        <div className="h-full flex items-center justify-between">
          {/* Verified social brand image + wordmark + brokerage sub-label */}
          <a href={resolve('#hero')} className="group flex min-h-11 items-center gap-3">
            <img
              src="/media/team/merritt-shawn-profile.jpg"
              alt=""
              aria-hidden="true"
              width={320}
              height={320}
              className="h-9 w-9 shrink-0 rounded-full border-2 border-white/80 object-cover shadow-sm transition-transform group-hover:scale-105"
            />
            <span className="flex flex-col leading-none">
              <span className="font-sans text-[13px] font-medium tracking-[0.08em] text-white md:text-[15px]">
                Merritt &amp; Shawn
              </span>
              <span className="mt-1 text-[7px] uppercase tracking-[0.18em] text-white/75 md:text-[8px]">
                Coastal Delaware Real Estate · Jack Lingo REALTOR®
              </span>
            </span>
          </a>

          {/* Desktop nav: uppercase condensed 12px + "+" mega-menu trigger */}
          <nav className="hidden lg:flex items-center gap-4 xl:gap-7" aria-label="Primary">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="font-display font-medium uppercase text-[12px] tracking-[0.12em] text-white/90 hover:text-accent-gold transition-colors"
              >
                {item.label}
              </a>
            ))}
            <button
              type="button"
              onClick={() => setIsMegaMenuOpen((open) => !open)}
              aria-expanded={isMegaMenuOpen}
              aria-label={isMegaMenuOpen ? 'Close menu' : 'Open menu'}
              className="ml-2 flex items-center justify-center w-8 h-8 border border-white/30 hover:border-accent-gold hover:text-accent-gold transition-colors"
            >
              <span
                className={`block text-lg leading-none transition-transform duration-300 ${
                  isMegaMenuOpen ? 'rotate-45' : ''
                }`}
              >
                +
              </span>
            </button>
          </nav>

          {/* Mobile hamburger */}
          <button
            type="button"
            className="-mr-2 inline-flex h-11 w-11 items-center justify-center text-current lg:hidden"
            onClick={() => setIsMobileMenuOpen((open) => !open)}
            aria-expanded={isMobileMenuOpen}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={isMobileMenuOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'}
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      {isMobileMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-primary-ink border-t border-white/10">
          <nav className="px-6 py-6 flex flex-col gap-4" aria-label="Mobile">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className="font-display uppercase text-lg tracking-[0.12em] text-white/90 hover:text-accent-gold transition-colors"
              >
                {item.label}
              </a>
            ))}
          </nav>
          <div className="flex flex-wrap gap-2 border-t border-white/10 px-6 py-5">
            <a href="https://www.instagram.com/merrittandshawn/" target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center gap-2 border border-white/15 px-3 font-sans text-[10px] font-medium uppercase tracking-[0.14em] text-white/70 hover:border-accent-gold hover:text-accent-gold"><Instagram size={15} aria-hidden="true" />Instagram</a>
            <a href="https://www.linkedin.com/in/merritt-cooch-2496bb125" target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center gap-2 border border-white/15 px-3 font-sans text-[10px] font-medium uppercase tracking-[0.14em] text-white/70 hover:border-accent-gold hover:text-accent-gold"><Linkedin size={15} aria-hidden="true" />Merritt</a>
            <a href="https://www.linkedin.com/in/shawn-mcdonnell-41587513b" target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center gap-2 border border-white/15 px-3 font-sans text-[10px] font-medium uppercase tracking-[0.14em] text-white/70 hover:border-accent-gold hover:text-accent-gold"><Linkedin size={15} aria-hidden="true" />Shawn</a>
          </div>
        </div>
      )}
    </header>

    {!isMobileMenuOpen && pathname === '/' && (
      <nav
        data-mobile-social-bar
        aria-label="Mobile social media"
        className={`fixed left-0 right-0 z-[45] flex h-11 items-center border-y border-white/10 bg-primary-ink/90 px-3 text-white shadow-lg backdrop-blur-md transition-[top] duration-300 md:hidden ${isScrolled ? 'top-[52px]' : 'top-16'}`}
      >
        <a href="https://www.instagram.com/merrittandshawn/" target="_blank" rel="noopener noreferrer" className="inline-flex h-11 flex-1 items-center justify-center gap-2 font-sans text-[9px] font-medium uppercase tracking-[0.16em] text-white/80 transition-colors hover:text-accent-gold">
          <Instagram size={14} className="text-accent-gold" aria-hidden="true" /> Instagram
        </a>
        <span className="h-4 w-px bg-white/20" aria-hidden="true" />
        <a href="https://www.linkedin.com/in/merritt-cooch-2496bb125" target="_blank" rel="noopener noreferrer" aria-label="Merritt Cooch on LinkedIn" className="inline-flex h-11 flex-1 items-center justify-center gap-2 font-sans text-[9px] font-medium uppercase tracking-[0.16em] text-white/80 transition-colors hover:text-accent-gold">
          <Linkedin size={14} className="text-accent-gold" aria-hidden="true" /> Merritt
        </a>
        <span className="h-4 w-px bg-white/20" aria-hidden="true" />
        <a href="https://www.linkedin.com/in/shawn-mcdonnell-41587513b" target="_blank" rel="noopener noreferrer" aria-label="Shawn McDonnell on LinkedIn" className="inline-flex h-11 flex-1 items-center justify-center gap-2 font-sans text-[9px] font-medium uppercase tracking-[0.16em] text-white/80 transition-colors hover:text-accent-gold">
          <Linkedin size={14} className="text-accent-gold" aria-hidden="true" /> Shawn
        </a>
        {!heroDetailsVisible && (
          <>
            <span className="h-4 w-px bg-white/20" aria-hidden="true" />
            <button
              data-mobile-hero-eye
              data-touch-accent-icon
              type="button"
              onClick={() => window.dispatchEvent(new Event(SHOW_HERO_DETAILS_EVENT))}
              aria-label="Show hero details"
              className="-mr-3 ml-1 inline-flex h-11 w-11 shrink-0 items-center justify-center border-l border-white/15 bg-white/[0.04] text-accent-gold transition-colors hover:bg-white/10 hover:text-white"
            >
              <Eye size={16} aria-hidden="true" />
            </button>
          </>
        )}
      </nav>
    )}

    {/* Full-screen mega-menu (desktop "+") — A.5; sibling so header bar stays clickable above it */}
    <MegaMenu open={isMegaMenuOpen} onClose={() => setIsMegaMenuOpen(false)} navItems={navItems} />
    </>
  )
}

export default Header
